import { useEffect, useState, useSyncExternalStore } from 'react';
import { useSession } from '@/contexts/SessionContext';
import { NUM_SLOTS, getStartHour } from '@/config/modeConfig';

export interface LiveNow {
  /** Fractional slot position (15-min slots) from the shift's left edge — the live time, or the ruler when pinned. */
  slot: number;
  /** HHMM at `slot`, e.g. "1145". */
  label: string;
  /** True while the user has dragged the line off live time and left it there. */
  pinned: boolean;
  /** Wall-clock HHMM right now (null outside the shift window) — what "back to now" returns to. */
  liveLabel: string | null;
}

// The ruler position is shared by the counter and officer graphs so both lines move together.
// null = follow the clock.
let pinnedSlot: number | null = null;
const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };
const getPinned = () => pinnedSlot;

/** Park the ruler at `slot` (clamped, snapped to 5 minutes). */
export function setRulerSlot(slot: number): void {
  const snapped = Math.round(Math.min(NUM_SLOTS, Math.max(0, slot)) * 3) / 3;
  if (snapped === pinnedSlot) return;
  pinnedSlot = snapped;
  listeners.forEach(l => l());
}
/** Send the ruler back to live time. */
export function resetRuler(): void {
  if (pinnedSlot === null) return;
  pinnedSlot = null;
  listeners.forEach(l => l());
}

const pad = (n: number) => String(n).padStart(2, '0');
function slotToHHMM(slot: number, shiftType: string | undefined): string {
  const total = Math.round(slot * 15);
  const h = (getStartHour(shiftType ?? 'morning') + Math.floor(total / 60)) % 24;
  return `${pad(h)}${pad(total % 60)}`;
}

/**
 * Where the vertical time line falls on the roster's time axis. By default it is
 * "now", re-evaluated every 15s so it creeps along with the clock (null outside
 * the shift window and in past/browse sessions, where a live marker is
 * meaningless). Dragging it (see LiveNowFlag) pins it as a ruler at the dragged
 * time until it is reset.
 */
export function useLiveNowSlot(shiftType: string | undefined): LiveNow | null {
  const { accessMode } = useSession();
  const pinned = useSyncExternalStore(subscribe, getPinned);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 15_000);
    // Phones suspend timers in the background — catch up the moment the tab returns.
    const onVisible = () => { if (!document.hidden) setNow(new Date()); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);
  // Esc drops the ruler back onto live time (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || pinnedSlot === null) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      resetRuler();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const startHour = getStartHour(shiftType ?? 'morning');
  const minutes = ((now.getHours() - startHour + 24) % 24) * 60 + now.getMinutes() + now.getSeconds() / 60;
  const liveSlot = minutes / 15;
  const liveOk = accessMode !== 'browse' && liveSlot >= 0 && liveSlot < NUM_SLOTS;
  const liveLabel = liveOk ? `${pad(now.getHours())}${pad(now.getMinutes())}` : null;
  if (pinned !== null) return { slot: pinned, label: slotToHHMM(pinned, shiftType), pinned: true, liveLabel };
  if (!liveOk) return null;
  return { slot: liveSlot, label: liveLabel!, pinned: false, liveLabel };
}
